import React, { useRef, useEffect, JSX } from 'react';
import Flickity from 'react-flickity-component';
import { classNames } from '@/helpers/common';
import Section from '@/model/Section';
import { EventDateSectionSite } from '../screens/Event';
import { ShouldShowModalProps } from '../types';

const flickityOptions = {
  freeScroll: true,
  percentPosition: true,
  cellAlign: 'left',
};

interface SliderProductAreaProps {
  items: EventDateSectionSite[];
  activeId?: string;
  handleOnClick: (item: Section) => void;
  showModal: (props: ShouldShowModalProps) => void;
  getEventKey: (sectionId: string, ticketId: string) => string;
  onModal: (sectionId: string, tickectId: string, hasMap: boolean) => void;
}

interface ProductAreaProps {
  item: EventDateSectionSite;
  isActive: boolean;
  sectionId: string;
  handleOnClick: (item: Section) => void;
  onSelect: () => void;
  onModal: (sectionId: string, tickectId: string, hasMap: boolean) => void;
}

const ProductAndArea: React.FC<ProductAreaProps> = ({
  item,
  isActive,
  sectionId,
  handleOnClick,
  onSelect,
  onModal,
}): JSX.Element => {
  const ref = useRef<HTMLAnchorElement>(null);

  useEffect((): (() => void) => {
    const applyClasses = (): void => {
      if (ref.current) {
        if (isActive) {
          ref.current.classList.add(
            'bg-[#60B9CD]',
            'text-white',
            'font-bold',
            'border-alert-info-soft',
          );
          ref.current.classList.remove(
            'bg-[#F1F1F1]',
            'text-[#828282]',
            'font-normal',
            'border-neutro-w-100',
          );

          const badge = ref.current.querySelector('.slider-section-badge');
          if (badge) {
            badge.classList.add('bg-neutro-w-000', 'text-alert-info-soft');
            badge.classList.remove('bg-neutro-w-400', 'text-neutro-w-000');
          }
        } else {
          ref.current.classList.add(
            'bg-[#F1F1F1]',
            'text-[#828282]',
            'font-normal',
            'border-neutro-w-100',
          );
          ref.current.classList.remove(
            'bg-[#60B9CD]',
            'text-white',
            'font-bold',
            'border-alert-info-soft',
          );

          const badge = ref.current.querySelector('.slider-section-badge');
          if (badge) {
            badge.classList.add('bg-neutro-w-400', 'text-neutro-w-000');
            badge.classList.remove('bg-neutro-w-000', 'text-alert-info-soft');
          }
        }
      }
    };

    applyClasses();
    const timeoutId = setTimeout(applyClasses, 100);
    return () => clearTimeout(timeoutId);
  }, [isActive]);

  return (
    <a
      ref={ref}
      key={item.section.id}
      onClick={() => {
        handleOnClick(item.section);
        onSelect();
        onModal(sectionId, item.tickets[0].id, item.showModal || false);
      }}
      className={classNames(
        isActive
          ? 'bg-[#60B9CD] text-white font-bold border-alert-info-soft'
          : 'bg-[#F1F1F1] text-[#828282] font-normal border-neutro-w-100',
        'carousel-cell w-[135px] mr-5 cursor-pointer p-2 flex flex-col justify-center items-center border hover:border-alert-info-soft',
      )}
    >
      {item.count > 0 && (
        <div
          className={classNames(
            'slider-section-badge w-[14px] h-[14px] rounded-full font-dmsans font-normal text-[10px] flex justify-center items-center absolute top-2 right-2',
            isActive ? 'bg-neutro-w-000 text-alert-info-soft' : 'bg-neutro-w-400 text-neutro-w-000',
          )}
        >
          {item.count}
        </div>
      )}

      {item.section.name && <span className="text-center">{item.section.name}</span>}
    </a>
  );
};

const SliderProductArea: React.FC<SliderProductAreaProps> = ({
  items,
  activeId,
  handleOnClick,
  onModal,
}): JSX.Element => {
  const [activeSectionId, setActiveSectionId] = React.useState<string>(
    activeId || (items.length > 0 ? String(items[0].section.id) : ''),
  );

  useEffect((): void => {
    if (activeId) {
      setActiveSectionId(String(activeId));
    } else if (items.length > 0) {
      setActiveSectionId(String(items[0].section.id));
    }
  }, [activeId, items]);

  return (
    <Flickity options={flickityOptions} className={'slider-ticket-product pl-[60px] mt-6'}>
      {items.map(item => {
        const isActive = String(item.section.id) === activeSectionId;
        return (
          <ProductAndArea
            key={item.section.id}
            item={item}
            isActive={isActive}
            handleOnClick={handleOnClick}
            onSelect={() => setActiveSectionId(String(item.section.id))}
            sectionId={item.section.id.toString()}
            onModal={onModal}
          />
        );
      })}
    </Flickity>
  );
};

export { SliderProductArea };
